import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

export interface EpcPrefixRule {
  prefix: string;
  matchType: 'hex' | 'ascii';
  description?: string;
  /** C1 — owning client for EPCs matching this prefix (null = pure filter). */
  clientId?: number | null;
  /** Resolved server-side at read time; never sent back for storage. */
  clientName?: string | null;
}

export interface EpcPrefixRulesResponse {
  rules: EpcPrefixRule[];
  rejectUnknownPrefix: boolean;
}

export interface SystemSettings {
  tenantDisplayName?: string | null;
  tenantDescription?: string | null;
  primaryColorHex?: string | null;
  mfaReminderDays: number;
  hasLogo: boolean;
  /** null/absent = no tenant override (fall back to deployment defaults). */
  features?: Record<string, boolean> | null;
}

export interface SaveSystemSettingsRequest {
  tenantDisplayName?: string | null;
  tenantDescription?: string | null;
  primaryColorHex?: string | null;
  mfaReminderDays: number;
  /** Omit/null = leave untouched; {} = clear override; non-empty = store. */
  features?: Record<string, boolean> | null;
}

// ── Client balance ("saldo") formula ─────────────────────────────────────────

/** How the "delivered" term is counted. */
export type DeliveredBasis = 'suppliedAmount' | 'scannedPallets';

export interface BalanceFormula {
  deliveredBasis: DeliveredBasis;
  includeTransferred: boolean;
  includeRecovered: boolean;
  includeLost: boolean;
  /** Recoveries returned to this location do not discharge the client. */
  excludeRecoveryLocationId?: number | null;
}

export interface BalanceFormulaResponse extends BalanceFormula {
  /** False = the tenant never saved one, so it is running on the default. */
  isConfigured: boolean;
  canEdit: boolean;
  expression: string;
}

export interface BalanceFormulaPreviewRow {
  clientId: number;
  clientName?: string | null;
  current: number;
  candidate: number;
  delta: number;
  terms: { delivered: number; transferred: number; recovered: number; lost: number };
}

export const entityConfigKeys = {
  all: ['entityConfig'] as const,
  epcPrefixRules: () => [...entityConfigKeys.all, 'epcPrefixRules'] as const,
  systemSettings: () => [...entityConfigKeys.all, 'systemSettings'] as const,
  logo: () => [...entityConfigKeys.all, 'logo'] as const,
  balanceFormula: () => [...entityConfigKeys.all, 'balanceFormula'] as const,
};

// ── EPC prefix rules ─────────────────────────────────────────────────────────

export function useEpcPrefixRules() {
  return useQuery({
    queryKey: entityConfigKeys.epcPrefixRules(),
    queryFn: async (): Promise<EpcPrefixRulesResponse> => {
      const response = await api.get<ApiEnvelope<EpcPrefixRule[]> & { rejectUnknownPrefix?: boolean }>(
        '/api/v2/entity-config/epc-prefix-rules'
      );
      return {
        rules: response.data.data ?? [],
        rejectUnknownPrefix: response.data.rejectUnknownPrefix ?? false,
      };
    },
  });
}

export function useSaveEpcPrefixRules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (rules: EpcPrefixRule[]) => {
      await api.post('/api/v2/entity-config/epc-prefix-rules', rules);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityConfigKeys.epcPrefixRules() });
    },
  });
}

/** C1 — registration policy for EPCs matching no configured prefix. */
export function useSaveEpcPrefixPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (rejectUnknownPrefix: boolean) => {
      await api.post('/api/v2/entity-config/epc-prefix-rules/policy', { rejectUnknownPrefix });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityConfigKeys.epcPrefixRules() });
    },
  });
}

// ── System settings (per-tenant identity/branding) ───────────────────────────

export function useSystemSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: entityConfigKeys.systemSettings(),
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await api.get<ApiEnvelope<SystemSettings>>(
        '/api/v2/entity-config/system-settings'
      );
      return response.data.data;
    },
  });
}

export function useSaveSystemSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: SaveSystemSettingsRequest) => {
      await api.post('/api/v2/entity-config/system-settings', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityConfigKeys.systemSettings() });
    },
  });
}

export function useUploadLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/api/v2/entity-config/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityConfigKeys.systemSettings() });
      queryClient.invalidateQueries({ queryKey: entityConfigKeys.logo() });
    },
  });
}

// ── Balance formula ──────────────────────────────────────────────────────────

export function useBalanceFormula() {
  return useQuery({
    queryKey: entityConfigKeys.balanceFormula(),
    queryFn: async (): Promise<BalanceFormulaResponse> => {
      const response = await api.get<ApiEnvelope<BalanceFormulaResponse>>(
        '/api/v2/entity-config/balance-formula'
      );
      return response.data.data;
    },
  });
}

export function useSaveBalanceFormula() {
  const queryClient = useQueryClient();
  return useMutation({
    /** Pass null to clear the override and return to the shipped default. */
    mutationFn: async (formula: BalanceFormula | null) => {
      await api.post('/api/v2/entity-config/balance-formula', formula);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: entityConfigKeys.balanceFormula() });
      // Saldos are on the wire in the clients list and the dashboard, and both
      // cache for minutes — without this they would disagree with the formula
      // the operator just saved.
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Compute a candidate formula against real data without saving it. This is what
 * makes changing the setting safe: the operator sees the resulting saldo, and
 * the delta against today's, before committing.
 */
export function usePreviewBalanceFormula() {
  return useMutation({
    mutationFn: async (input: { formula: BalanceFormula; clientId?: number | null; take?: number }) => {
      const response = await api.post<
        ApiEnvelope<{ expression: string; rows: BalanceFormulaPreviewRow[] }>
      >('/api/v2/entity-config/balance-formula/preview', {
        formula: input.formula,
        clientId: input.clientId ?? null,
        take: input.take ?? 5,
      });
      return response.data.data;
    },
  });
}

/**
 * Fetch the current tenant's logo as an object URL (the GET /logo endpoint is
 * authenticated, so we fetch it as a blob rather than putting it in an <img src>).
 * Only runs when `enabled` (i.e. the tenant has a logo).
 */
export function useTenantLogo(enabled: boolean) {
  return useQuery({
    queryKey: entityConfigKeys.logo(),
    enabled,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const res = await api.get('/api/v2/entity-config/logo', { responseType: 'blob' });
      return URL.createObjectURL(res.data as Blob);
    },
  });
}
